import { CssBaseline, ThemeProvider } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  HashRouter as Router,
  Routes,
} from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute"; // Import ProtectedRoute
import ToastProvider from "./components/Toast";
import Activation from "./containers/activations";
import AddDoctor from "./containers/addDoctor";
import AddOrder from "./containers/AddOrder";
import AirSettings from "./containers/AirSettings";
import Analytics from "./containers/Analytics";
import Dashboard from "./containers/dashboard";
import Doctor from "./containers/doctor";
import DoctorDetail from "./containers/DoctorDetail";
import EngagementDetail from "./containers/EngagementDetail";
import EngagementTracker from "./containers/EngagementTracker";
import EngagementOverview from "./containers/EngagementOverview";
import EngagementMyDay from "./containers/EngagementMyDay";
import EngagementClinics from "./containers/EngagementClinics";
import EngagementClinicDetail from "./containers/EngagementClinicDetail";
import EngagementRenewals from "./containers/EngagementRenewals";
import EngagementCamps from "./containers/EngagementCamps";
import EngagementTickets from "./containers/EngagementTickets";
import EngagementTeamOverview from "./containers/EngagementTeamOverview";
import EngagementSettings from "./containers/EngagementSettings";
import Kiosk from "./containers/kiosk";
import KioskDetails from "./containers/kioskDetails";
import LoginPage from "./containers/Login";
import MyTasks from "./containers/myTasks";
import NeedsAction from "./containers/needsAction";
import ActionQueue from "./containers/ActionQueue";
import OnboardingCalls from "./containers/OnboardingCalls";
import PurchaseAgreements from "./containers/PurchaseAgreements";
import ReadyToShip from "./containers/ReadyToShip";
import Shipments from "./containers/Shipments";
import Technicians from "./containers/Technicians";
import TravelPlan from "./containers/TravelPlan";
import ShippedOrders from "./containers/ShippedOrders";
import Checklists from "./containers/Checklists";
import MyTravel from "./containers/MyTravel";
import Inaugurations from "./containers/Inaugurations";
import ExpenseApprovals from "./containers/ExpenseApprovals";
import MyExpenses from "./containers/MyExpenses";
import Drafts from "./containers/Drafts";
import TeamPipeline from "./containers/TeamPipeline";
import StalledOrders from "./containers/StalledOrders";
import Specialists from "./containers/Specialists";
import ZohoSync from "./containers/ZohoSync";
import Receipts from "./containers/Receipts";
import Orders from "./containers/orders";
import OrderDetail from "./containers/orderDetails";
import PaymentLinks from "./containers/paymentLinks";
import ClinicPaymentLinks from "./containers/clinicPaymentLinks";
import ProfilePage from "./containers/ProfilePage";
import Quotations from "./containers/quotations";
import QuotationDetail from "./containers/quotationDetail";
import Settings from "./containers/Settings";
import Shipment from "./containers/shipment";
import ShipmentTracker from "./containers/ShipmentTracker";
import SubscriptionLogs from "./containers/SubscriptionLogs";
import Support from "./containers/Support";
import SupportChat from "./containers/SupportChat";
import TicketLogs from "./containers/TicketLogs";
import TestPage from "./containers/testPage";
import Users from "./containers/users";
import UserTests from "./containers/userTests";
import Scans from "./containers/Scans";
import Tags from "./containers/Tags";
import { getTheme } from "./theme";
import { setUnreadCount, setUserInfo, setPermissions } from "./store/rootReducer";
import { useDispatch } from "react-redux";
import axios from "axios";
import offlineQueue from "./Services/offlineQueue";
import { OFFLINE_DISPATCH } from "./Services/api.service";
const App = () => {
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem("theme") || "light";
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuthCheck, setIsLoadingAuthCheck] = useState(true);
  const dispatch = useDispatch();
 useEffect(() => {
   const token = localStorage.getItem("token");

   if (token) {
     axios
       .get(
         `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/me/permissions`,
         {
           headers: { Authorization: `Bearer ${token}` },
         },
       )
       .then((res) => {
         dispatch(setUserInfo(res.data.userInfo));
         dispatch(setPermissions(res.data.permissions));
         setIsAuthenticated(true); // Restore login state on refresh
       })
       .catch(() => {
         localStorage.removeItem("token");
         setIsAuthenticated(false); // Token invalid/expired → force login
       })
       .finally(() => {
         setIsLoadingAuthCheck(false);
       });
   } else {
     setIsAuthenticated(false); // First visit → force login
     setIsLoadingAuthCheck(false);
   }
 }, []);

  // Flow 7 — flush any technician actions queued while offline as soon as
  // the app opens (in case it was already back online since last time), and
  // again every time the browser regains connectivity.
  useEffect(() => {
    offlineQueue.startAutoSync(OFFLINE_DISPATCH);
  }, []);

  // Chrome/Firefox change a focused <input type="number">'s value on scroll
  // instead of scrolling the page — surprising on a form field a user is
  // just trying to scroll past. Blur it on wheel so the value never changes
  // underneath them; the scroll itself proceeds normally once unfocused.
  useEffect(() => {
    const blurNumberInputOnWheel = () => {
      const el = document.activeElement;
      if (el?.tagName === "INPUT" && el.type === "number") {
        el.blur();
      }
    };
    document.addEventListener("wheel", blurNumberInputOnWheel, { passive: true });
    return () => document.removeEventListener("wheel", blurNumberInputOnWheel);
  }, []);

  const toggleTheme = () => {
    setThemeMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      localStorage.setItem("theme", newMode);
      return newMode;
    });
  };
  const theme = getTheme(themeMode);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setIsAuthenticated(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <Router>
          <Routes>
            {/* Redirect from '/' to '/login' */}
            <Route path="/" element={<Navigate to="/login" replace />} />

            {/* Login route */}
            <Route
              path="/login"
              element={<LoginPage onLogin={handleLogin} />}
            />

            {/* Protected routes */}
            <Route
              path="/shipmentTracker"
              element={
                <ProtectedRoute
                  isAuthenticated={isAuthenticated}
                  isLoadingAuthCheck={isLoadingAuthCheck}
                >
                  <ShipmentTracker />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute
                  isAuthenticated={isAuthenticated}
                  isLoadingAuthCheck={isLoadingAuthCheck}
                >
                  <Dashboard onLogout={handleLogout} />
                </ProtectedRoute>
              }
            >
              <Route
                path="/dashboard/doctor"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <Doctor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/orders/add-order"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <AddOrder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/orders/:id"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <OrderDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/orders"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <Orders />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/payment-links"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <PaymentLinks />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/payment-links/:clinicId"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <ClinicPaymentLinks />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/quotations"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <Quotations />
                  </ProtectedRoute>
                }
              />
                            <Route
                path="/dashboard/quotations/:id"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <QuotationDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/needs-action"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <NeedsAction />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/my-tasks"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <MyTasks />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/queue"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="actionItems"
                  >
                    <ActionQueue />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engagement-officer/onboarding-calls"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="engagementOfficer"
                  >
                    <OnboardingCalls />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/purchase-agreements"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="agreementActions"
                  >
                    <PurchaseAgreements />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/ready-to-ship"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="readyToShip"
                  >
                    <ReadyToShip />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/shipments"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="predispatch"
                  >
                    <Shipments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/technicians"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="installation"
                  >
                    <Technicians />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/travel-plan"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="installation"
                  >
                    <TravelPlan />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/shipped-orders"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="shippedOrders"
                  >
                    <ShippedOrders />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/checklists"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="technicianJobs"
                  >
                    <Checklists />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/my-travel"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="technicianJobs"
                  >
                    <MyTravel />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/expense-approvals"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="expenseClaimsApprove"
                  >
                    <ExpenseApprovals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/my-expenses"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="expenseClaimsFile"
                  >
                    <MyExpenses />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/inaugurations"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="inaugurations"
                  >
                    <Inaugurations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/drafts"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="ordersCreate"
                  >
                    <Drafts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/pipeline"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="pipeline"
                  >
                    <TeamPipeline />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/stalled-orders"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="pipeline"
                  >
                    <StalledOrders />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/specialists"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="pipeline"
                  >
                    <Specialists />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/zoho-sync"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="accounts"
                  >
                    <ZohoSync />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/receipts"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="accounts"
                  >
                    <Receipts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/support"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <Support />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/support/chat/:id"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <SupportChat />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/support/ticket/:id/logs"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <TicketLogs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engagementTracker"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <EngagementTracker />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engagementTracker/:id"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <EngagementDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/user/test/:id"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="users"
                  >
                    <TestPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/user"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="users"
                  >
                    <Users />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/scans"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <Scans />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/user/:id"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="users"
                  >
                    <UserTests />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard/engage"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <Doctor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard/Shipment"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <Shipment />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/Activation"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <Activation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/settings"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <Settings themeMode={themeMode} toggleTheme={toggleTheme} onLogout={handleLogout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/profile"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/analytics"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/doctor/add-doctor"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <AddDoctor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/doctor/doctorDetails/:id"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <DoctorDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/doctor/doctorDetails/:id/logs/:subId"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <SubscriptionLogs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engage/doctorDetails/:id/settings"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <AirSettings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard/engage/doctorDetails/:id"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <DoctorDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/kiosk"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <Kiosk />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/kiosk/kioskDetails/:id"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <KioskDetails />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard/tags"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                  >
                    <Tags />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engagement/overview"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="engagementOverview"
                  >
                    <EngagementOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engagement/my-day"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="engagementMyDay"
                  >
                    <EngagementMyDay />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engagement/clinics"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="engagementClinics"
                  >
                    <EngagementClinics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engagement/clinics/:doctorId"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="engagementClinics"
                  >
                    <EngagementClinicDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engagement/renewals"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="engagementRenewals"
                  >
                    <EngagementRenewals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engagement/camps"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="engagementCamps"
                  >
                    <EngagementCamps />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engagement/tickets"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="engagementTickets"
                  >
                    <EngagementTickets />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engagement/team"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="engagementTeamOverview"
                  >
                    <EngagementTeamOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/engagement/settings"
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthenticated}
                    isLoadingAuthCheck={isLoadingAuthCheck}
                    requiredPermission="engagementSettings"
                  >
                    <EngagementSettings />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Routes>
        </Router>
      </ToastProvider>
    </ThemeProvider>
  );
};

export default App;
